import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DEFAULT_ICP_URL,
  configuredSiteName,
  hasConfiguredLoginBackground,
  resolveDocumentTitle,
  resolveFooterCopyright,
  resolveSiteBrandMediaUrl,
  type SiteBrandView,
} from '@/constants/site-brand'
import { useAuthConfig } from './useAuthConfig'

const EMPTY: SiteBrandView = {}

/** 租户站点品牌(名称 / Logo / 页脚备案);default 门户用租户 site 配置 */
export function useSiteBrand() {
  const { t } = useTranslation()
  const { config, loading, error, reload } = useAuthConfig()

  const site = useMemo(() => config?.site ?? EMPTY, [config?.site])

  const brandName = useMemo(
    () => configuredSiteName(site, config?.tenant_name),
    [site, config?.tenant_name],
  )

  const hasLogo = useMemo(() => Boolean(resolveSiteBrandMediaUrl(site.logo_url)), [site.logo_url])

  /** 顶栏旁 / 页脚主名：仅运营配置的站点名；无配置时供无 Logo 场景兜底 */
  const siteName = useMemo(() => {
    if (brandName) return brandName
    const tenant = config?.tenant_name?.trim()
    if (tenant) return tenant
    return t('app.name')
  }, [brandName, config?.tenant_name, t])

  /** 页脚是否展示站点名行：有站点名/组织名，或无 Logo 时用兜底名 */
  const showFooterSiteName = useMemo(() => {
    if (brandName) return true
    if (!hasLogo) return true
    return Boolean(config?.tenant_name?.trim())
  }, [brandName, hasLogo, config?.tenant_name])

  const tagline = useMemo(() => {
    const custom = site.tagline?.trim()
    if (custom) return custom
    return t('app.tagline')
  }, [site.tagline, t])

  const copyright = useMemo(
    () =>
      resolveFooterCopyright({
        custom: site.copyright,
        brandName,
        tenantName: config?.tenant_name,
        appName: t('app.name'),
        translateDefault: (year, name) => t('site.footer.copyright', { year, name }),
      }),
    [site.copyright, brandName, config?.tenant_name, t],
  )

  const icpLink = useMemo(() => {
    if (!site.icp_number?.trim()) return null
    const url = site.icp_url?.trim() || DEFAULT_ICP_URL
    return { number: site.icp_number.trim(), url }
  }, [site.icp_number, site.icp_url])

  const psbLink = useMemo(() => {
    if (!site.psb_number?.trim()) return null
    const url = site.psb_url?.trim()
    if (!url) return { number: site.psb_number.trim(), url: undefined }
    return { number: site.psb_number.trim(), url }
  }, [site.psb_number, site.psb_url])

  const documentTitle = useMemo(
    () =>
      resolveDocumentTitle({
        brandName,
        tenantName: config?.tenant_name,
        appName: t('app.name'),
        hasLogo,
      }),
    [brandName, config?.tenant_name, hasLogo, t],
  )

  const hasLoginBackground = useMemo(() => hasConfiguredLoginBackground(site), [site])

  return {
    site,
    /** 运营配置的站点名；空表示未配置 */
    brandName,
    hasLogo,
    showFooterSiteName,
    /** auth/config.tenant_name，供品牌位区分回填 */
    tenantName: config?.tenant_name?.trim() || '',
    siteName,
    tagline,
    copyright,
    icpLink,
    psbLink,
    documentTitle,
    hasLoginBackground,
    loading,
    error,
    reload,
  }
}
